import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/theme/ThemeProvider";
import ThemeCustomizer from "@/components/theme/ThemeCustomizer";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import POS from "@/pages/POS";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import PdaBarcodes from "@/components/PdaBarcodes";
import Categories from "@/pages/Categories";
import Users from "@/pages/Users";
import Contacts from '@/pages/Contacts';
import Transactions from "@/pages/Transactions";
import Purchases from "@/pages/Purchases";
import PrinterSettings from "@/pages/PrinterSettings";
import ShortcutSettings from '@/pages/ShortcutSettings';
import Stores from "@/pages/Stores";
import Reports from "@/pages/Reports";
import Piutang from "@/pages/Piutang";
import BranchStock from '@/pages/BranchStock';
import StockRequests from '@/pages/StockRequests';
import BranchTransfers from '@/pages/BranchTransfers';
import Stok from "@/pages/Stok";
import Shifts from "@/pages/Shifts";
import CashierDevices from "@/pages/CashierDevices";
import Backup from "@/pages/Backup";
import PaymentMethods from "@/pages/PaymentMethods";
import Tenants from "@/pages/Tenants";
import SystemStatus from "@/pages/SystemStatus";
import BackupSettings from "@/pages/BackupSettings";
import Platform from '@/pages/Platform';
import PublicStore from '@/pages/PublicStore';
import AuditLogs from "@/pages/AuditLogs";
import Warehouse from '@/pages/Warehouse';
import WhatsApp from '@/pages/WhatsApp';
import StockAlerts from '@/pages/StockAlerts';
import SupervisorApprovals from '@/pages/SupervisorApprovals';
import Promotions from '@/pages/Promotions';
import PurchaseSuggestions from '@/pages/PurchaseSuggestions';
import GudangApp from '@/pages/GudangApp';
import { isTransferA5Profile } from '@/lib/printer-mode';
import { canAccessPath } from '@/lib/user-permissions';
function Splash() {
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );
}

function Protected({ children, adminOnly, masterOnly, warehouseOnly }) {
  const { user, loading, tenant, logout } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  const custom = user.permission_mode === 'custom' && user.role !== 'admin';
  if (custom && location.pathname !== '/no-access' && !canAccessPath(user, location.pathname)) return <main className="m-auto max-w-lg p-6 text-center space-y-3"><h1 className="text-xl font-bold">Akses belum diberikan</h1><p className="text-sm text-ui-muted">Minta admin toko mengatur hak akses akun ini.</p><button type="button" className="theme-action min-h-10 px-4" onClick={() => { logout(); window.location.assign('/login'); }}>Keluar</button></main>;
  if (!custom && user.role === "warehouse" && !["/warehouse", "/products", "/barcode-pda", "/stock-requests", "/branch-transfers", "/login"].includes(location.pathname)) return <Navigate to="/warehouse" replace />;
  if (warehouseOnly && !["admin", "warehouse"].includes(user.role) && !custom) return <Navigate to="/pos" replace />;
  if (adminOnly && user.role !== "admin" && !custom) return <Navigate to="/pos" replace />;
  if (masterOnly && (tenant?.kind !== "master" || user.role !== "admin")) return <Navigate to="/pos" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading, tenant, tenantError } = useAuth();
  const location = useLocation();
  if (tenantError) return <main className="p-8" role="alert">{tenantError}</main>;
  if (!tenant) return <Splash />;
  if (isTransferA5Profile() && !['/branch-transfers', '/login'].includes(location.pathname)) return <Navigate to="/branch-transfers" replace />;
  return (<Routes>
    <Route path="/gudang/*" element={<GudangApp />} />
    <Route path="/no-access" element={<Protected><main className="m-auto p-6 text-center">Akses tidak diberikan.</main></Protected>} />
    <Route path="/barcode-pda" element={<Protected warehouseOnly><PdaBarcodes /></Protected>} />
    <Route path="/login" element={loading ? <Splash /> : user ? <Navigate to="/" replace /> : <Login />} />
    <Route element={<Protected><AppLayout /></Protected>}>
      <Route path="/contacts" element={<Contacts />} />
      <Route path="/audit-logs" element={<Protected adminOnly><AuditLogs /></Protected>} />
      <Route path="/appearance" element={<Protected adminOnly><ThemeCustomizer /></Protected>} />
      <Route path="/shortcut-settings" element={<Protected adminOnly><ShortcutSettings /></Protected>} />
      <Route path="/master/platform" element={<Protected masterOnly><Platform /></Protected>} />
      <Route path="/branch-stock" element={<Protected adminOnly><BranchStock /></Protected>} />
      <Route path="/devices" element={<Protected adminOnly><CashierDevices /></Protected>} />
      <Route path="/stock-requests" element={<StockRequests />} />
      <Route path="/branch-transfers" element={<BranchTransfers />} />
      <Route path="/warehouse" element={<Protected warehouseOnly><Warehouse /></Protected>} />
      <Route path="/whatsapp" element={<Protected adminOnly><WhatsApp /></Protected>} />
      <Route path="/stock-alerts" element={<Protected adminOnly><StockAlerts /></Protected>} />
      <Route path="/supervisor" element={<Protected adminOnly><SupervisorApprovals /></Protected>} />
      <Route path="/promotions" element={<Protected adminOnly><Promotions /></Protected>} />
      <Route path="/purchase-suggestions" element={<Protected adminOnly><PurchaseSuggestions /></Protected>} />
      <Route path="/master/tenants" element={<Protected masterOnly><Tenants /></Protected>} />
      <Route path="/master/system" element={<Protected masterOnly><SystemStatus /></Protected>} />
      <Route path="/master/backup" element={<Protected masterOnly><BackupSettings /></Protected>} />
      <Route path="/" element={<Navigate to="/pos" replace />} /><Route path="/pos" element={<POS />} />
      <Route path="/dashboard" element={<Protected adminOnly><Dashboard /></Protected>} /><Route path="/reports" element={<Protected adminOnly><Reports /></Protected>} /><Route path="/shifts" element={<Protected adminOnly><Shifts /></Protected>} /><Route path="/purchases" element={<Protected adminOnly><Purchases /></Protected>} /><Route path="/suppliers" element={<Protected adminOnly><Navigate to="/users?tab=suppliers" replace /></Protected>} /><Route path="/products" element={<Protected warehouseOnly><Products /></Protected>} /><Route path="/stok" element={<Protected adminOnly><Stok /></Protected>} /><Route path="/categories" element={<Protected adminOnly><Categories /></Protected>} /><Route path="/users" element={<Protected adminOnly><Users /></Protected>} /><Route path="/settings" element={<Protected adminOnly><PrinterSettings /></Protected>} /><Route path="/payment-methods" element={<Protected adminOnly><PaymentMethods /></Protected>} /><Route path="/stores" element={<Protected adminOnly><Stores /></Protected>} /><Route path="/backup" element={<Protected adminOnly><Backup /></Protected>} /><Route path="/transactions" element={<Transactions />} /><Route path="/piutang" element={<Piutang />} />
    </Route><Route path="*" element={<Navigate to="/" replace />} />
  </Routes>);
}
function App() {
  if (window.location.hostname === "store.masivi.id") return <PublicStore />;
  return (
    <div className="App min-h-screen">
      <ThemeProvider><AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>

      </AuthProvider></ThemeProvider>
    </div>
  );
}

export default App;
